import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  X,
  Wallet,
  Coins,
  CreditCard,
  Key,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { walletService } from '../../services/walletService';
import { formatCoins } from '../../utils/walletLabels';
import { cardTheme } from '../../styles/cardTheme';

interface InsufficientWalletModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentBalance: number;
  coinsRequired: number;
}

const EGYPT_TOP_UP_METHODS = [
  'البطاقات البنكية (فيزا / ماستركارد / ميزة)',
  'المحافظ الإلكترونية (فودافون كاش، اتصالات كاش، أورانج كاش)',
] as const;

const InsufficientWalletModal: React.FC<InsufficientWalletModalProps> = ({
  isOpen,
  onClose,
  currentBalance,
  coinsRequired,
}) => {
  const [isCharging, setIsCharging] = useState(false);
  const [chargeError, setChargeError] = useState<string | null>(null);

  if (!isOpen) return null;

  const shortage = Math.max(0, coinsRequired - currentBalance);

  const handleChargeShortage = async () => {
    setChargeError(null);
    setIsCharging(true);
    const result = await walletService.chargeWallet(shortage, 'EGP');
    setIsCharging(false);

    if (result.success && result.data.paymentSession.sessionUrl) {
      window.location.href = result.data.paymentSession.sessionUrl;
      return;
    }
    setChargeError(result.success ? 'لم يتم العثور على رابط الدفع' : result.error);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
      dir="rtl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="insufficient-wallet-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-label="إغلاق"
      />

      <div className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-green-100">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 p-4 sm:p-5 border-b border-green-100 bg-white">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${cardTheme.iconBoxGold}`}>
              <Wallet className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 id="insufficient-wallet-title" className={`text-lg font-bold ${cardTheme.title}`}>
                رصيد المحفظة غير كافٍ
              </h2>
              <p className={`text-sm mt-0.5 ${cardTheme.subtitle}`}>
                محتاج تشحن محفظتك عشان تكمل الشراء
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-2 rounded-lg hover:bg-gray-100 transition-colors min-h-11 min-w-11 flex items-center justify-center"
            aria-label="إغلاق"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-4">
          {/* Balance summary — EGP */}
          <div className="rounded-xl border border-green-100 bg-gradient-to-br from-green-50/80 to-gold-50/40 p-4 space-y-3">
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">رصيدك الحالي</span>
              <span className="font-bold text-green-800 flex items-center gap-1">
                <Coins className="w-4 h-4 text-gold-600" />
                {formatCoins(currentBalance)} عملة
              </span>
            </div>
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">تكلفة الكورس</span>
              <span className="font-bold text-gray-900">
                {formatCoins(coinsRequired)} جنيه مصري
              </span>
            </div>
            <div className="h-px bg-gold-300/60" />
            <div className="flex justify-between items-center gap-3">
              <span className="text-sm font-semibold text-red-700">النقص المطلوب شحنه</span>
              <span className="text-lg font-bold text-red-600">
                {formatCoins(shortage)} جنيه
              </span>
            </div>
            <p className="text-xs text-gray-500">1 عملة = 1 جنيه مصري (EGP)</p>
          </div>

          {/* Egyptian payment methods info */}
          <div className={`${cardTheme.shell} p-4`}>
            <h3 className={`text-sm font-bold mb-2 ${cardTheme.title}`}>
              طرق الشحن المتاحة في مصر
            </h3>
            <ul className="space-y-1.5 text-sm text-gray-600">
              {EGYPT_TOP_UP_METHODS.map((method) => (
                <li key={method} className="flex items-start gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gold-500 shrink-0" />
                  {method}
                </li>
              ))}
            </ul>
          </div>

          {chargeError && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              {chargeError}
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2">
            <button
              type="button"
              onClick={handleChargeShortage}
              disabled={isCharging || shortage <= 0}
              className={`w-full min-h-11 py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50 ${cardTheme.btnPrimary}`}
            >
              {isCharging ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  جاري التحويل لبوابة الدفع...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  شحن {formatCoins(shortage)} جنيه عبر بوابة الدفع
                </>
              )}
            </button>

            <Link
              to="/student-portal/wallet"
              onClick={onClose}
              className={`w-full min-h-11 py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 ${cardTheme.btnGold}`}
            >
              <Key className="w-5 h-5" />
              شحن بكود المحفظة أو المحفظة
            </Link>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-full min-h-11 py-2.5 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
          >
            إلغاء والعودة للدفع
          </button>
        </div>
      </div>
    </div>
  );
};

export default InsufficientWalletModal;
